import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { GET_PRODUCT_FOR_PDP } from '../graphql/operations'
import { useAuth } from '../auth/AuthContext'
import { useCart } from '../lib/cart'
import { useVoiceAvatar, type AssistantState } from '../lib/voiceAvatar'
import { formatPrice } from '../lib/format'
import { avatarEnabled } from '../lib/featureFlags'

interface Variant {
  id: string
  title: string
  price: number
  compareAtPrice: number | null
  sku: string | null
  option1: string | null
  option2: string | null
  option3: string | null
  inventoryQuantity: number
  available: boolean
  imageUrls: string[] | null
}
interface ProductPDP {
  id: string
  title: string
  description: string | null
  handle: string
  currency: string
  status: string
  imageUrls: string[] | null
  productVariants: Variant[]
  stores: { id: string; name: string }[]
}

const STATE_COLOR: Record<AssistantState, string> = {
  idle: '#64748b',
  listening: '#34d399',
  thinking: '#fbbf24',
  speaking: '#38bdf8',
}

export default function ProductDetail() {
  const { id } = useParams()
  const { token, user } = useAuth()
  const cart = useCart()
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  // Explicitly clicked thumbnail. null → derive the hero from the selected
  // variant's first image (or the product gallery).
  const [heroImage, setHeroImage] = useState<string | null>(null)

  const { data, loading, error } = useQuery<{ product: ProductPDP | null }>(GET_PRODUCT_FOR_PDP, {
    variables: { id },
    skip: !id,
  })

  const product = data?.product ?? null
  const storeId = product?.stores?.[0]?.id ?? null

  const avatar = useVoiceAvatar({
    productId: product?.id ?? '',
    storeId: storeId ?? '',
    token,
    // When the stylist adds/removes on the buyer's behalf, refetch the cart.
    onCartUpdated: cart.refresh,
  })

  if (loading) return <div className="pdp-loading" data-testid="pdp-loading">Loading product…</div>
  if (error) return <div className="error" data-testid="pdp-error">Couldn’t load this product: {error.message}</div>
  if (!product) return <div className="error" data-testid="pdp-missing">Product not found.</div>

  const prices = product.productVariants.map((v) => v.price).filter((p) => p > 0)
  const minPrice = prices.length ? Math.min(...prices) : null
  const canTalk = Boolean(storeId) && product.status === 'active'
  // When the stylist is on, the right column becomes the avatar and the buy
  // box drops below the fold.
  const avatarActive = avatar.phase === 'connecting' || avatar.phase === 'live'

  // Default the selection to the first in-stock variant (or the first one).
  const selectedVariant =
    product.productVariants.find((v) => v.id === selectedVariantId) ??
    product.productVariants.find((v) => v.available) ??
    product.productVariants[0]

  // Thumbnails show EVERY variant's images, each tagged with its owning
  // variant so a click both shows the photo and selects that colour. We build
  // from the variants (not the product gallery) so the thumbnail URLs match
  // the per-variant images exactly — they're separate Active Storage blobs,
  // so a product-gallery URL would never line up with a variant's. Sizes of
  // one colour share the same blob, so we dedupe by URL.
  const seenThumb = new Set<string>()
  const thumbs: { url: string; variantId: string | null }[] = []
  for (const v of product.productVariants) {
    for (const url of v.imageUrls ?? []) {
      if (seenThumb.has(url)) continue
      seenThumb.add(url)
      thumbs.push({ url, variantId: v.id })
    }
  }
  // Fall back to the product gallery when no variant carries images.
  if (thumbs.length === 0) {
    for (const url of product.imageUrls ?? []) thumbs.push({ url, variantId: null })
  }
  // Hero = the explicitly-clicked thumbnail, else the selected variant's own
  // photo (so it swaps on colour change), else the first available image.
  const displayedHero =
    heroImage ?? selectedVariant?.imageUrls?.[0] ?? thumbs[0]?.url ?? product.imageUrls?.[0]

  function selectVariant(id: string) {
    setSelectedVariantId(id)
    setHeroImage(null)
  }

  // Clicking a thumbnail shows that image and, when it belongs to a variant,
  // selects that colour too.
  function pickThumb(url: string, variantId: string | null) {
    setHeroImage(url)
    if (variantId) setSelectedVariantId(variantId)
  }

  async function addToBasket() {
    if (!selectedVariant) return
    setAddError(null)
    const res = await cart.add(selectedVariant.id, qty)
    if (res.ok) {
      setAdded(true)
      setTimeout(() => setAdded(false), 2000)
    } else {
      setAddError(res.error ?? 'Could not add to basket')
    }
  }

  // The buy box (title, price, description, options, add-to-basket). Lives in
  // the right column normally, and drops below the fold while the stylist is
  // on screen — rendered in exactly one place at a time.
  const buyBox = (
    <>
      <h1 className="pdp-title" data-testid="pdp-title">{product.title}</h1>
      {minPrice != null && (
        <p className="pdp-price" data-testid="pdp-price">{formatPrice(minPrice, product.currency)}</p>
      )}
      {product.description && <p className="pdp-description">{product.description}</p>}

      {product.productVariants.length > 0 && (
        <div className="pdp-variants" data-testid="pdp-variants">
          <h2 className="pdp-subhead">Options</h2>
          <ul className="pdp-variant-list">
            {product.productVariants.map((v) => {
              const isSelected = selectedVariant?.id === v.id
              const label = [v.option1, v.option2, v.option3].filter(Boolean).join(' / ') || v.title
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`pdp-variant ${isSelected ? 'is-selected' : ''} ${v.available ? '' : 'is-unavailable'}`}
                    disabled={!v.available}
                    onClick={() => selectVariant(v.id)}
                  >
                    <span>{label}</span>
                    <span className="pdp-variant-price">{formatPrice(v.price, product.currency)}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <div className="pdp-buy" data-testid="pdp-buy">
        <div className="pdp-qty">
          <button type="button" aria-label="Decrease quantity" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
          <span data-testid="pdp-qty">{qty}</span>
          <button type="button" aria-label="Increase quantity" onClick={() => setQty((q) => q + 1)}>+</button>
        </div>
        <button
          className="btn-primary pdp-add"
          data-testid="add-to-basket"
          disabled={!selectedVariant || !selectedVariant.available}
          onClick={addToBasket}
        >
          {added ? 'Added ✓' : 'Add to basket'}
        </button>
      </div>
      {addError && <p className="error" data-testid="add-error">{addError}</p>}

      {/* Live stylist entry point — only shown when no session is running. */}
      {!avatarActive && (
        <button
          className="btn-secondary pdp-talk"
          data-testid="talk-to-assistant"
          disabled={!canTalk || !avatarEnabled}
          onClick={avatar.start}
          title={avatarEnabled ? undefined : 'The live stylist is currently offline'}
        >
          {!avatarEnabled
            ? 'Live stylist unavailable'
            : user ? 'Talk to a stylist' : 'Ask our stylist — no sign-in needed'}
        </button>
      )}
      {avatar.phase === 'error' && <p className="error" data-testid="avatar-error">{avatar.error}</p>}
    </>
  )

  // The live stylist: video/voice orb + push-to-talk + transcript. Takes the
  // right column while a session is connecting or live.
  const avatarPanel = (
    <section className="avatar-stage avatar-stage--column" data-testid="avatar-stage">
      <div className="avatar-stage-header">
        <span className="avatar-state" data-testid="assistant-state">
          <span className="avatar-state-dot" style={{ background: STATE_COLOR[avatar.assistantState] }} />
          {avatar.phase === 'connecting' ? 'connecting…' : avatar.assistantState}
        </span>
        <button className="btn-danger" data-testid="end-session" onClick={avatar.stop}>End</button>
      </div>

      {/* The video div is owned imperatively (the hook calls replaceChildren
          on it when an agent video track arrives), so it must have NO React
          children. Placeholders live as sibling overlays instead — and the
          voice-only orb shows when the agent runs without a Simli face. */}
      <div className="avatar-stage-video">
        <div className="avatar-video" ref={avatar.avatarRef} data-testid="avatar-video" />
        {avatar.phase === 'connecting' && (
          <div className="avatar-overlay"><span className="avatar-connecting">Connecting avatar…</span></div>
        )}
        {avatar.phase === 'live' && !avatar.hasVideo && (
          <div className="avatar-overlay" data-testid="avatar-voiceonly">
            <span className="voice-orb" style={{ background: STATE_COLOR[avatar.assistantState] }} />
            <span className="avatar-connecting">Voice stylist · {avatar.assistantState}</span>
          </div>
        )}
      </div>

      {/* While the stylist is thinking/speaking it's not the buyer's turn
          (no barge-in), so swap the talk button for a status indicator. */}
      {avatar.assistantState === 'thinking' || avatar.assistantState === 'speaking' ? (
        <div className="ptt ptt-responding" data-testid="ptt-responding">
          {avatar.assistantState === 'speaking' ? 'Speaking…' : 'Thinking…'}
        </div>
      ) : (
        <button
          className={`ptt ${avatar.micActive ? 'is-live' : ''}`}
          data-testid="ptt-button"
          disabled={avatar.phase !== 'live'}
          onPointerDown={avatar.pttStart}
          onPointerUp={avatar.pttStop}
          onPointerLeave={() => { if (avatar.micActive) avatar.pttStop() }}
        >
          {avatar.micActive ? 'Listening — release to send' : 'Hold to talk'}
        </button>
      )}

      {avatar.transcript.length > 0 && (
        <ul className="avatar-transcript" data-testid="avatar-transcript">
          {avatar.transcript.map((t, i) => (
            <li key={i} className={`transcript-row transcript-${t.speaker}`}>
              <span className="transcript-speaker">{t.speaker}</span>
              <span>{t.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )

  return (
    <div className="pdp" data-testid="pdp">
      <div className="pdp-grid">
        {/* ---- Left: product media (hero + selectable thumbnails) ---- */}
        <section className="pdp-media">
          {displayedHero ? (
            <img src={displayedHero} alt={product.title} className="pdp-hero" data-testid="pdp-hero" />
          ) : (
            <div className="pdp-hero pdp-hero--empty">No image</div>
          )}
          {thumbs.length > 1 && (
            <div className="pdp-thumbs" data-testid="pdp-thumbs">
              {thumbs.map(({ url, variantId }) => (
                <button
                  key={url}
                  type="button"
                  className={`pdp-thumb ${displayedHero === url ? 'is-selected' : ''}`}
                  onClick={() => pickThumb(url, variantId)}
                  aria-label="Show this image"
                >
                  <img src={url} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ---- Right: the stylist while talking, otherwise the buy box ---- */}
        <section className="pdp-right">
          {avatarActive ? avatarPanel : <div className="pdp-info">{buyBox}</div>}
        </section>
      </div>

      {/* ---- Below the fold: the buy box drops here during a session ---- */}
      {avatarActive && (
        <section className="pdp-info pdp-belowfold" data-testid="pdp-belowfold">
          {buyBox}
        </section>
      )}
    </div>
  )
}
